// A matter's Drive folder: what it holds, and pulling one of those files
// into the matter. Both endpoints are admin-only on the server, so the
// surface hides the control for everyone else rather than letting a call
// come back 403.

import type { CaseDriveFolder } from "@duralex/shared";
import { getAuthHeaders } from "./auth";

async function driveJson<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `${fallback} (HTTP ${res.status})`);
  }
  return (await res.json()) as T;
}

export async function fetchCaseDriveFolder(caseId: string): Promise<CaseDriveFolder> {
  const res = await fetch(`/api/drive/cases/${encodeURIComponent(caseId)}/folder`, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return driveJson<CaseDriveFolder>(res, "Could not read the Drive folder");
}

export interface DriveImportResult {
  /** False when the matter already held the file - the server answers that
      after hashing the bytes, which is more than the listing can know. */
  imported: boolean;
  extracted: boolean;
  message: string;
}

export async function importDriveFile(fileId: string, caseId: string): Promise<DriveImportResult> {
  const res = await fetch(`/api/drive/files/${encodeURIComponent(fileId)}/import`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify({ caseId }),
  });
  return driveJson<DriveImportResult>(res, "Import failed");
}

/** The firm-wide import run: one pass over the least recently read case
    folders. The server keeps a single set of counters, so these are the
    numbers of the run in flight or of the last one this process saw. */
export interface DriveImportRun {
  running: boolean;
  filesImported: number;
  documentsExtracted: number;
  casesLooked: number;
  /** Files a folder still holds past the per-case ceiling of this run. */
  filesRemaining: number;
  /** Matters this run put on the firm's client list. */
  clientsLinked: number;
}

export async function fetchDriveImportRun(): Promise<DriveImportRun> {
  const res = await fetch("/api/drive/extract/status", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return driveJson<DriveImportRun>(res, "Could not read the import status");
}

export async function startDriveImportRun(
  maxCases: number,
): Promise<{ started: boolean; message: string }> {
  const res = await fetch(`/api/drive/sync-and-extract?maxCases=${maxCases}`, {
    method: "POST",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return driveJson<{ started: boolean; message: string }>(res, "Could not start the import");
}
